/* ==========================================================================
   VOLLEYCHAMP theme.js
   Small, dependency-free web components. Each component is self-contained
   and only activates when its custom element is on the page.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};

  /* ---------- Helpers ---------- */

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const lockScroll = (lock) => {
    document.documentElement.classList.toggle('has-drawer', lock);
    document.querySelectorAll('vc-sticky-bar').forEach((bar) => bar.update && bar.update());
  };

  /* ---------- Cart API ---------- */

  const Cart = {
    /** All cart writes run one after another. Parallel requests could be
        answered out of order and leave stale HTML on the page. */
    queue: Promise.resolve(),
    enqueue(task) {
      const run = this.queue.then(task, task);
      this.queue = run.catch(() => {});
      return run;
    },

    sectionsToRender() {
      const ids = new Set(['cart-drawer']);
      document.querySelectorAll('vc-cart-items[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
      return [...ids];
    },

    async parse(response) {
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.cartError);
      }
      return data;
    },

    request(url, body) {
      return this.enqueue(() => fetch(`${url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionsToRender(), sections_url: window.location.pathname })
      }).then((response) => this.parse(response)));
    },

    /** Submit a product form as-is (Dawn-style), so app inputs like Pumper's
        quantity and line item properties reach the cart unchanged. */
    addForm(formData) {
      formData.set('sections', this.sectionsToRender().join(','));
      formData.set('sections_url', window.location.pathname);
      return this.enqueue(() => fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      }).then((response) => this.parse(response)));
    },

    /** Change a line by its stable key (item.key), not by position: positions
        shift as soon as another line is removed. */
    change(key, quantity) { return this.request(theme.routes.cartChange, { id: key, quantity }); },

    /** Replace every cart-related region on the page with fresh server HTML. */
    render(sections) {
      if (!sections) return;
      const hadFocus = document.activeElement && document.activeElement.closest('[data-cart-region]');
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        document.querySelectorAll(`[data-cart-region="${id}"]`).forEach((target) => {
          const source = doc.querySelector(`[data-cart-region="${id}"]`);
          if (!source) return;
          // The order note lives inside the region: keep what the visitor typed
          // but has not submitted yet, instead of resetting it to the saved value.
          const note = target.querySelector('textarea[name="note"]');
          const noteValue = note ? note.value : null;
          target.innerHTML = source.innerHTML;
          const freshNote = target.querySelector('textarea[name="note"]');
          if (freshNote && noteValue !== null) freshNote.value = noteValue;
        });
        const countSource = doc.querySelector('[data-cart-count]');
        if (countSource) {
          document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = countSource.textContent; });
        }
      });
      // The focused control was replaced - move focus back into the open drawer.
      if (hadFocus && !document.activeElement.closest('[data-cart-region]')) {
        const drawer = document.querySelector('vc-drawer.is-open');
        const target = drawer && drawer.querySelector('[data-drawer-focus]');
        if (target) target.focus();
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
  };
  window.VCCart = Cart;

  /* ---------- Drawer (menu + cart) ---------- */

  class VCDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    /** Keep keyboard focus inside the open drawer (it is a modal dialog). */
    trapFocus(event) {
      const panel = this.querySelector('.drawer__panel') || this;
      const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      const focusTarget = this.querySelector('[data-drawer-focus]') || this.querySelector('button, a, input');
      if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this.opener && this.opener.focus) this.opener.focus();
    }
  }
  customElements.define('vc-drawer', VCDrawer);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* ---------- Cart items (drawer + cart page) ---------- */

  class VCCartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cart-change]');
        if (!button) return;
        event.preventDefault();
        this.update(button.dataset.key, Number(button.dataset.quantity));
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-quantity]');
        if (!input) return;
        this.update(input.dataset.key, Math.max(0, parseInt(input.value, 10) || 0));
      });
    }

    /** Every control is locked while a change is in flight; the fresh HTML
        from the server replaces them (unlocked) once the change is done. */
    setBusy(busy) {
      if (busy) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      this.querySelectorAll('[data-cart-change], [data-cart-quantity]').forEach((el) => { el.disabled = busy; });
    }

    async update(key, quantity) {
      if (!key || this.hasAttribute('aria-busy')) return;
      this.setBusy(true);
      try {
        const cart = await Cart.change(key, quantity);
        Cart.render(cart.sections);
      } catch (error) {
        const target = this.querySelector('[data-cart-error]');
        if (target) target.textContent = error.message;
      } finally {
        this.setBusy(false);
      }
    }
  }
  customElements.define('vc-cart-items', VCCartItems);

  /* ---------- Product form ----------
     Volume prices are handled by the Pumper Bundles widget; this component
     keeps the button state in sync with the selected variant and submits
     the form via AJAX so the cart drawer opens. */

  class VCProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[action*="/cart/add"]');
      if (!this.form) return;
      this.variantInput = this.form.querySelector('[name="id"]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.errorBox = this.querySelector('[data-form-error]');
      this.variants = JSON.parse(this.querySelector('[data-variants-json]')?.textContent || '[]');

      this.addEventListener('change', (event) => {
        if (event.target === this.variantInput) this.onVariantChange();
      });
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.updateButton();
    }

    get currentVariant() {
      const id = Number(this.variantInput?.value);
      return this.variants.find((variant) => variant.id === id) || this.variants[0];
    }

    onVariantChange() {
      const variant = this.currentVariant;
      if (!variant) return;
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      this.updateButton();
      this.updatePrice(variant);
      this.updateMedia(variant);
    }

    /** Price block and gallery sit outside the form, in the same buy box. */
    get buyBox() { return this.closest('[data-buy-box]') || this; }

    updatePrice(variant) {
      const price = this.buyBox.querySelector('[data-variant-price]');
      if (price && variant.price) price.textContent = variant.price;
      const compare = this.buyBox.querySelector('[data-variant-compare-price]');
      if (compare) {
        compare.textContent = variant.comparePrice || '';
        compare.hidden = !variant.comparePrice;
      }
    }

    updateMedia(variant) {
      const gallery = this.buyBox.querySelector('vc-gallery');
      if (gallery && gallery.goTo && variant.mediaPosition > 0) gallery.goTo(variant.mediaPosition - 1);
    }

    updateButton() {
      const variant = this.currentVariant;
      if (!variant || !this.submitButton) return;
      // Stay disabled while a request is running; the label may still change.
      this.submitButton.disabled = this.submitting || !variant.available;
      const label = this.submitButton.querySelector('[data-button-label]');
      if (label) label.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
    }

    async onSubmit(event) {
      if (!window.fetch) return;
      event.preventDefault();
      // Disabled covers both "sold out" and "request in flight", so a second
      // Enter/click on a slow connection cannot add the item twice.
      if (this.submitting || this.submitButton.disabled) return;
      this.submitting = true;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.classList.add('is-loading');
      if (this.errorBox) this.errorBox.textContent = '';
      try {
        const formData = new FormData(this.form);
        // Apps (e.g. Pumper) may render their inputs next to the form instead
        // of inside it - pick those up from the surrounding buy box as well.
        const scope = this.closest('[data-buy-box]') || this;
        scope.querySelectorAll('input[name^="properties["], input[name="quantity"]').forEach((input) => {
          if (input.form === this.form || input.disabled) return;
          if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
          if (input.value !== '') formData.set(input.name, input.value);
        });
        const quantity = parseInt(formData.get('quantity'), 10);
        formData.set('quantity', quantity > 0 ? String(quantity) : '1');
        const response = await Cart.addForm(formData);
        Cart.render(response.sections);
        const drawer = document.getElementById('CartDrawer');
        if (drawer && drawer.open) drawer.open(this.submitButton);
        else window.location.href = theme.routes.cart;
      } catch (error) {
        if (this.errorBox) this.errorBox.textContent = error.message;
      } finally {
        this.submitting = false;
        this.submitButton.classList.remove('is-loading');
        this.submitButton.removeAttribute('aria-busy');
        this.updateButton(); // re-enables the button unless the variant is sold out
      }
    }
  }
  customElements.define('vc-product-form', VCProductForm);

  /* ---------- Media gallery ---------- */

  class VCGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
      this.deferMedia();
      this.autoplayVideos();
      if (!this.track || this.slides.length < 2) return;

      [...this.thumbs, ...this.dots].forEach((control) => {
        control.addEventListener('click', () => this.goTo(Number(control.dataset.index)));
      });

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setActive(this.slides.indexOf(entry.target));
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    disconnectedCallback() {
      [this.observer, this.deferObserver, this.videoObserver].forEach((observer) => observer && observer.disconnect());
    }

    /** Gallery media marked data-defer-media gets its real source once the
        gallery is near the viewport and the page has finished loading (or as
        soon as the visitor interacts with it). Keeps bandwidth free for the
        LCP image. */
    deferMedia() {
      const deferred = [...this.querySelectorAll('[data-defer-media]')];
      if (deferred.length === 0) return;
      let done = false;
      const load = () => {
        if (done) return;
        done = true;
        deferred.forEach((el) => {
          if (el.dataset.srcset) el.srcset = el.dataset.srcset;
          if (el.dataset.poster) el.poster = el.dataset.poster;
          el.removeAttribute('data-defer-media');
        });
      };
      const whenLoaded = () => {
        if (document.readyState === 'complete') load();
        else window.addEventListener('load', load, { once: true });
      };
      ['pointerdown', 'focusin', 'scroll'].forEach((type) => this.addEventListener(type, load, { once: true, passive: true, capture: true }));
      if (!('IntersectionObserver' in window)) { whenLoaded(); return; }
      this.deferObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.deferObserver.disconnect();
        whenLoaded();
      }, { rootMargin: '400px 0px' });
      this.deferObserver.observe(this);
    }

    /** Short clips marked data-autoplay play muted and looped like a GIF,
        but only while they are visible (saves data and CPU). With reduced
        motion they get normal controls instead. */
    autoplayVideos() {
      const videos = [...this.querySelectorAll('video[data-autoplay]')];
      if (videos.length === 0) return;
      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        videos.forEach((video) => video.setAttribute('controls', ''));
        return;
      }
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            video.muted = true;
            const playing = video.play();
            if (playing && playing.catch) playing.catch(() => video.setAttribute('controls', ''));
          } else {
            video.pause();
          }
        });
      }, { threshold: [0, 0.6] });
      videos.forEach((video) => this.videoObserver.observe(video));
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      this.setActive(index);
    }

    setActive(index) {
      [this.thumbs, this.dots].forEach((list) => list.forEach((el, i) => el.setAttribute('aria-current', i === index ? 'true' : 'false')));
      this.slides.forEach((slide, i) => {
        if (i !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }
  }
  customElements.define('vc-gallery', VCGallery);

  /* ---------- Background video (e.g. "Steps" section) ----------
     <vc-video data-src-sd data-src-hd data-src-full> wraps a <video> that
     has no src yet. The clip loads and plays (muted, looped) only while it
     is near the viewport, with the SD rendition on phones. With reduced
     motion or data saver it gets a source and normal controls instead; if a
     rendition fails to load, the full file is used. */

  class VCVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.muted = true;
      this.video.addEventListener('error', () => this.useFallback());

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = navigator.connection && navigator.connection.saveData;
      if (reducedMotion || saveData || !('IntersectionObserver' in window)) {
        this.load();
        this.video.controls = true;
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.load();
          this.play();
        } else if (this.video.getAttribute('src')) {
          this.video.pause();
        }
      }, { rootMargin: '200px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    get preferredSource() {
      const hd = window.matchMedia('(min-width: 750px)').matches;
      return (hd ? this.dataset.srcHd : this.dataset.srcSd) || this.dataset.srcFull;
    }

    load() {
      if (!this.video.getAttribute('src') && this.preferredSource) this.video.src = this.preferredSource;
    }

    play() {
      const playing = this.video.play();
      if (playing && playing.catch) playing.catch(() => {});
    }

    useFallback() {
      const full = this.dataset.srcFull;
      if (!full || this.video.getAttribute('src') === full) return;
      this.video.src = full;
      this.play();
    }
  }
  customElements.define('vc-video', VCVideo);

  /* ---------- Sticky buy bar (mobile) ---------- */

  class VCStickyBar extends HTMLElement {
    connectedCallback() {
      // Visible once the page is scrolled and the main buy button is out of
      // view. IntersectionObserver avoids layout reads on every scroll event.
      this.targetVisible = false;
      this.scrolled = window.scrollY > 500;
      const target = document.querySelector('[data-sticky-target]');
      if (target && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.targetVisible = entries[0].isIntersecting;
          this.update();
        });
        this.observer.observe(target);
      }
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.scrolled = window.scrollY > 500;
          this.update();
        });
      };
      this.onCartUpdated = () => this.update();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('cart:updated', this.onCartUpdated);
      this.update();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    update() {
      const drawerOpen = document.documentElement.classList.contains('has-drawer');
      this.classList.toggle('is-visible', this.scrolled && !this.targetVisible && !drawerOpen);
    }
  }
  customElements.define('vc-sticky-bar', VCStickyBar);

  /* ---------- Rotating announcement (mobile) ---------- */

  class VCRotator extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-rotator-item]')];
      if (this.items.length === 0) return;
      this.index = 0;
      this.items[0].classList.add('is-active');
      if (this.items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = setInterval(() => {
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }, 4000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('vc-rotator', VCRotator);

  /* ---------- YouTube modal ---------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-youtube-id]');
    if (!trigger) return;
    const modal = document.getElementById('VideoModal');
    if (!modal || typeof modal.showModal !== 'function') return;
    event.preventDefault();
    const frame = modal.querySelector('[data-video-frame]');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(trigger.dataset.youtubeId)}?autoplay=1&rel=0&playsinline=1`;
    iframe.title = trigger.dataset.videoTitle || 'Video';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.setAttribute('allowfullscreen', '');
    frame.replaceChildren(iframe);
    modal.showModal();
  });

  /* Remove the player whenever the dialog closes (close button, backdrop
     click or Escape), so the video never keeps playing in the background. */
  const resetVideoModal = (modal) => {
    const frame = modal && modal.querySelector('[data-video-frame]');
    if (frame) frame.innerHTML = '';
  };

  const bindVideoModal = () => {
    const modal = document.getElementById('VideoModal');
    if (!modal || modal.dataset.bound) return;
    modal.dataset.bound = 'true';
    modal.addEventListener('close', () => resetVideoModal(modal));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindVideoModal);
  else bindVideoModal();

  document.addEventListener('click', (event) => {
    const modal = event.target.closest('#VideoModal');
    if (modal && (event.target === modal || event.target.closest('[data-video-close]'))) {
      modal.close();
      resetVideoModal(modal);
    }
  });

  /* ---------- Buy links ----------
     Links marked with data-buy-link point to the product page. If the current
     page already has a buy box, scroll to it instead of leaving the page. */

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-buy-link]');
    if (!link) return;
    const buyBox = document.querySelector('[data-buy-box]');
    if (!buyBox) return;
    event.preventDefault();
    const drawer = link.closest('vc-drawer');
    if (drawer) drawer.close();
    buyBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /* ---------- Page-level initialisers ---------- */

  const init = () => {
    // Address forms: all_country_option_tags renders no selection, so pick
    // the saved country (the option's text is the country name).
    document.querySelectorAll('select[data-default]').forEach((select) => {
      const wanted = select.dataset.default;
      if (!wanted) return;
      const option = [...select.options].find((o) => o.value === wanted || o.text === wanted);
      if (option) option.selected = true;
    });

    // Decorative background videos (hero) stay still for visitors who prefer
    // reduced motion; the poster image remains visible.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('video[data-background-video]').forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* ---------- Auto-submit selects (language switcher) ---------- */

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-auto-submit]')) event.target.form.submit();
  });
})();
